// Màn chặn "hồ sơ chưa đủ": bọc khung ứng dụng (DashboardLayout). Đơn vị chưa khai
// đủ chủ tài khoản / kho lấy hàng / tài khoản ngân hàng thì mọi trang trong app đưa
// về /onboarding — trừ Cài đặt, Thông báo, Góp ý. Đơn vị quản trị và phiên chưa
// chọn shop không bị chặn. Lỗi khi kiểm tra → cho qua (server vẫn chặn tạo đơn).

import type { ReactNode } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { ClipboardList } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { logout } from '@/lib/auth'
import { useQuery } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { useAuthState } from '@/hooks/useAuthState'
import { fetchProfileStatus, isAdminTenant, isProfileGateExempt, profileQueryKey } from '@/lib/onboarding'
import { Spinner } from '@/components/ui/spinner'

export function ProfileGate({ children }: { children: ReactNode }) {
  const auth = useAuthState()
  const location = useLocation()
  const shop = auth.shops.find((s) => s.shopId === auth.activeShopId)
  const applies = auth.isAuthenticated && auth.is2FaVerified && !!auth.activeShopId && !!shop && !isAdminTenant(shop.tenantType)

  const q = useQuery({
    queryKey: profileQueryKey(auth.activeShopId),
    queryFn: fetchProfileStatus,
    enabled: applies,
    staleTime: 60_000,
    // 4xx (chưa chọn shop, không có quyền…) không thử lại — cho qua luôn.
    retry: (count, err) => count < 1 && !(err instanceof ApiError && err.statusCode < 500),
  })

  if (!applies) return <>{children}</>
  if (q.isPending) {
    return (
      <div className="min-h-screen grid place-items-center bg-paper text-muted">
        <div className="flex items-center gap-2 text-sm">
          <Spinner />
          Đang kiểm tra thông tin tài khoản…
        </div>
      </div>
    )
  }
  if (q.isError || q.data.complete || isProfileGateExempt(location.pathname)) return <>{children}</>
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): hồ sơ là việc của chủ — báo rõ thay vì đưa sang màn khai hồ sơ
  if (shop.role === 'staff') return <StaffProfileNotice items={q.data.items.filter((i) => !i.done).map((i) => i.label)} />
  return <Navigate to="/onboarding" replace state={{ from: location.pathname }} />
}

function StaffProfileNotice({ items }: { items: string[] }) {
  const nav = useNavigate()
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper p-6">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-5 grid size-14 place-items-center rounded-full bg-warn/10 text-warn">
          <ClipboardList className="size-7" aria-hidden />
        </div>
        <h1 className="display-md">Đơn vị chưa hoàn tất hồ sơ</h1>
        <p className="mt-2 text-sm text-inkSoft">
          Chủ đơn vị cần khai đủ {items.length ? items.join(', ').toLowerCase() : 'thông tin'}.
        </p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button type="button" variant="outline" className="h-11" onClick={() => window.location.reload()}>Tải lại</Button>
          <Button type="button" variant="outline" className="h-11" onClick={() => nav('/settings')}>Cài đặt</Button>
          <Button type="button" variant="ghost" className="h-11" onClick={async () => { await logout(); nav('/login', { replace: true }) }}>Đăng xuất</Button>
        </div>
      </div>
    </div>
  )
}
